/**
 * Recipient targeting system for AI Email Writer
 * Class-based architecture for different government bodies
 */

import { SupervisorsByName } from '../../electeds/supervisors';

// Helper to merge comma-separated email lists, trim, and dedupe (case-insensitive)
const combineEmails = (...lists) => {
  const seen = new Set();
  const result = [];
  for (const list of lists) {
    if (!list) continue;
    const parts = Array.isArray(list) ? list : String(list).split(',');
    for (const raw of parts) {
      const email = String(raw).trim();
      if (!email) continue;
      const key = email.toLowerCase();
      if (!seen.has(key)) {
        seen.add(key);
        result.push(email);
      }
    }
  }
  return result.join(',');
};

// Recipient targeting strategies
export const RECIPIENT_TYPES = {
  STATIC: 'static',
  SUPERVISORS: 'supervisors',
  PLANNING_COMMISSION: 'planning-commission',
  LAND_USE_COMMITTEE: 'land-use-committee',
  SCHOOL_BOARD: 'school-board',
  // Add more as needed
};

/**
 * Base class for recipient targeting
 */
class RecipientTargeter {
  constructor() {
    this.name = 'Generic Recipients';
  }

  /**
   * Get default recipient emails (all members)
   */
  getRecipients() {
    return [];
  }

  /**
   * Get default CC emails
   */
  getCCs() {
    return [];
  }

  /**
   * Get recipient name/title
   */
  getRecipientName() {
    return this.name;
  }

  /**
   * Get targeted recipients based on user's district/location
   */
  getTargetedRecipients(district) {
    // Default: no targeting, return all recipients
    return {
      recipientEmail: this.getRecipients().join(','),
      ccEmail: this.getCCs().join(','),
      recipient: this.getRecipientName()
    };
  }

  /**
   * Check if this targeter supports dynamic targeting
   */
  supportsDynamicTargeting() {
    return false;
  }
}

/**
 * San Francisco Board of Supervisors targeting
 */
class SupervisorsTargeter extends RecipientTargeter {
  constructor() {
    super();
    this.name = 'Board of Supervisors';
  }

  getRecipients() {
    return Object.values(SupervisorsByName).map(supe => supe.email);
  }

  getCCs() {
    return Object.values(SupervisorsByName).map(supe => supe.staffEmail);
  }

  getTargetedRecipients(district) {
    if (district) {
      // Find the supervisor for this district
      const supervisor = Object.values(SupervisorsByName).find(
        s => s.district === district.toString()
      );
      
      if (supervisor) {
        // Target specific supervisor, CC all others + staff
        const otherSupervisors = Object.values(SupervisorsByName)
          .filter(s => s.district !== district.toString())
          .map(s => s.email);
        
        return {
          recipientEmail: supervisor.email,
          ccEmail: [...otherSupervisors, ...this.getCCs()].join(','),
          recipient: `Supervisor ${supervisor.lastName}`
        };
      }
    }
    
    // Fallback to all supervisors
    return {
      recipientEmail: this.getRecipients().join(','),
      ccEmail: this.getCCs().join(','),
      recipient: this.getRecipientName()
    };
  }

  supportsDynamicTargeting() {
    return true;
  }
}

/**
 * Static recipient targeting (manual override or pre-configured groups)
 */
class StaticTargeter extends RecipientTargeter {
  constructor({ name = 'Recipients', recipients = [], ccs = [] } = {}) {
    super();
    this.name = name;
    this.recipients = recipients;
    this.ccs = ccs;
  }

  getRecipients() {
    return this.recipients;
  }

  getCCs() {
    return this.ccs;
  }
}

/**
 * Targeter registry
 */
const TARGETERS = {
  [RECIPIENT_TYPES.SUPERVISORS]: new SupervisorsTargeter(),
  [RECIPIENT_TYPES.PLANNING_COMMISSION]: new StaticTargeter({
    name: 'San Francisco Planning Commission',
    recipients: [
      'lydia.so@sfgov.org',
      'kathrin.moore@sfgov.org',
      'derek.braun@sfgov.org',
      'amy.campbell@sfgov.org',
      'theresa.imperial@sfgov.org',
      'sean.mcgarry@sfgov.org',
      'gilbert.a.williams@sfgov.org',
    ],
    ccs: ['commissions.secretary@sfgov.org', 'jonas.ionin@sfgov.org'],
  }),
  [RECIPIENT_TYPES.LAND_USE_COMMITTEE]: new StaticTargeter({
    name: 'Board of Supervisors Land Use Committee',
    recipients: [
      SupervisorsByName['Myrna Melgar'],
      SupervisorsByName['Bilal Mahmood'],
      SupervisorsByName['Chyanne Chen'],
    ]
      .filter(Boolean)
      .map((member) => member.email),
    ccs: [
      SupervisorsByName['Myrna Melgar'],
      SupervisorsByName['Bilal Mahmood'],
      SupervisorsByName['Chyanne Chen'],
    ]
      .filter(Boolean)
      .map((member) => member.staffEmail)
      .filter(Boolean),
  }),
  [RECIPIENT_TYPES.SCHOOL_BOARD]: new StaticTargeter({
    name: 'San Francisco Board of Education',
    recipients: [
      'philkim@sfusd.edu',
      'jaimehuling@sfusd.edu',
      'MattAlexander@sfusd.edu',
      'alidafisher@sfusd.edu',
      'paraggupta@sfusd.edu',
      'supryiaray@sfusd.edu',
      'lisaweissman-ward@sfusd.edu',
      'mariasu@sfusd.edu',
    ],
  }),
  [RECIPIENT_TYPES.STATIC]: new StaticTargeter(),
};

/**
 * Get targeter instance for a recipient type
 */
export const getTargeter = (recipientType) => {
  return TARGETERS[recipientType] || TARGETERS[RECIPIENT_TYPES.STATIC];
};

/**
 * Get default emails for a recipient type
 */
export const getDefaultEmails = (recipientType) => {
  const targeter = getTargeter(recipientType);
  
  if (recipientType === RECIPIENT_TYPES.STATIC) {
    return {
      recipientEmail: null,
      ccEmail: null,
      recipient: targeter.getRecipientName()
    };
  }
  
  return {
    recipientEmail: targeter.getRecipients().join(','),
    ccEmail: targeter.getCCs().join(','),
    recipient: targeter.getRecipientName()
  };
};

/**
 * Get recipient emails based on type and user's district
 */
export const getRecipientEmails = (recipientType, district, fallbackRecipientEmail, fallbackCcEmail) => {
  const targeter = getTargeter(recipientType);
  
  // For static type, use provided fallbacks
  if (recipientType === RECIPIENT_TYPES.STATIC) {
    return {
      recipientEmail: fallbackRecipientEmail,
      ccEmail: fallbackCcEmail,
      recipient: targeter.getRecipientName()
    };
  }
  
  // Get targeted results
  const targeted = targeter.getTargetedRecipients(district);
  
  // Use fallbacks if provided, otherwise use targeter defaults
  return {
    recipientEmail: fallbackRecipientEmail || targeted.recipientEmail,
    // Always append provided ccEmail to dynamic targeter CCs (and dedupe)
    ccEmail: combineEmails(targeted.ccEmail, fallbackCcEmail),
    recipient: targeted.recipient,
  };
};

/**
 * Check if a recipient type supports dynamic targeting
 */
export const supportsDynamicTargeting = (recipientType) => {
  const targeter = getTargeter(recipientType);
  return targeter.supportsDynamicTargeting();
};
